/**
 * Pfeiltasten im Erfassungsraster — wie in Excel.
 *
 * Ein Handler am `<tbody>` (Ereignisse der Felder steigen dorthin auf), keiner je
 * Feld: Die Felder hängen ihre Tasten schon an `useFeld` (Enter speichert), und
 * die Navigation braucht ohnehin die Nachbarzeilen.
 *
 * - **↑ / ↓** springen in dieselbe Spalte der Zeile darüber bzw. darunter —
 *   auch aus einer Auswahl heraus, deren Wert sonst mit den Pfeilen blättert
 *   (Alt+↓ öffnet sie weiterhin). **Enter** springt wie in Excel nach unten.
 * - **← / →** springen zum Nachbarfeld der Zeile, in einem Textfeld aber erst,
 *   wenn die Schreibmarke am Rand steht. Wer ein Feld betritt, hat den Inhalt
 *   markiert und springt also sofort weiter; wer darin korrigiert, bewegt erst
 *   die Marke.
 *
 * Gespeichert wird nebenbei: Der Fokuswechsel ist ein Verlassen des Felds.
 */
import type { KeyboardEvent } from 'react';

const FELDER = 'input:not([disabled]):not([type="hidden"]), select:not([disabled])';

type Steuerung = HTMLInputElement | HTMLSelectElement;

function felderIn(el: Element): Steuerung[] {
  return Array.from(el.querySelectorAll<Steuerung>(FELDER));
}

/** Ob ein Textfeld seine Schreibmarke selbst bewegen will, statt die Zelle zu verlassen. */
function bewegtMarke(el: Steuerung, richtung: 'links' | 'rechts'): boolean {
  if (!(el instanceof HTMLInputElement) || el.type === 'checkbox') return false;
  const { selectionStart: a, selectionEnd: b, value } = el;
  if (a === null || b === null) return false;
  if (a === 0 && b === value.length) return false; // alles markiert → wie frisch betreten
  return richtung === 'links' ? !(a === 0 && b === 0) : !(a === value.length && b === value.length);
}

function betrete(el: Steuerung) {
  el.focus();
  if (el instanceof HTMLInputElement && el.type !== 'checkbox') el.select();
}

/** Feld derselben Spalte in der nächsten Zeile, die dort eines hat. */
function senkrecht(von: Steuerung, schritt: 1 | -1): Steuerung | null {
  const zelle = von.closest('td');
  const zeile = zelle?.parentElement;
  if (!zelle || !zeile) return null;
  const spalte = (zelle as HTMLTableCellElement).cellIndex;
  const innen = felderIn(zelle).indexOf(von);
  let nachbar = schritt === 1 ? zeile.nextElementSibling : zeile.previousElementSibling;
  while (nachbar) {
    const dort = (nachbar as HTMLTableRowElement).cells?.[spalte];
    const kandidaten = dort ? felderIn(dort) : [];
    // Zweites Feld einer Zelle (Umfang, Termindauer) fehlt, wo nichts gewählt ist — dann das erste.
    if (kandidaten.length > 0) return kandidaten[Math.min(innen, kandidaten.length - 1)];
    nachbar = schritt === 1 ? nachbar.nextElementSibling : nachbar.previousElementSibling;
  }
  return null;
}

function waagrecht(von: Steuerung, schritt: 1 | -1): Steuerung | null {
  const zeile = von.closest('tr');
  if (!zeile) return null;
  const felder = felderIn(zeile);
  return felder[felder.indexOf(von) + schritt] ?? null;
}

export function rasterTaste(e: KeyboardEvent<HTMLElement>) {
  if (e.ctrlKey || e.metaKey || e.shiftKey || e.nativeEvent.isComposing) return;
  const el = e.target;
  if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) return;

  let ziel: Steuerung | null = null;
  switch (e.key) {
    case 'ArrowDown':
      if (e.altKey) return; // Auswahl aufklappen
      ziel = senkrecht(el, 1);
      break;
    case 'ArrowUp':
      if (e.altKey) return;
      ziel = senkrecht(el, -1);
      break;
    case 'Enter':
      ziel = senkrecht(el, 1);
      break;
    case 'ArrowLeft':
      if (e.altKey || bewegtMarke(el, 'links')) return;
      ziel = waagrecht(el, -1);
      break;
    case 'ArrowRight':
      if (e.altKey || bewegtMarke(el, 'rechts')) return;
      ziel = waagrecht(el, 1);
      break;
    default:
      return;
  }
  // Auch ohne Ziel (Rand des Rasters): Eine Auswahl soll nicht still ihren Wert wechseln.
  e.preventDefault();
  if (ziel) betrete(ziel);
}
